/**
 * RequestApprovalType entity validation rules.
 */

import { getEntityAttr, getEntityAttrString } from './ReusableValidation';
import type { ValidationWarning } from './ReusableValidation';

const TAGS_MAX_LENGTH = 448;

const CLONE_DISPLAY_NAME_WARNING =
    'You are cloning this Request Approval Type. Please change the Display Name to avoid conflicts with the original.';

const TAGS_LENGTH_WARNING = (len: number) =>
    `Tags exceed ${TAGS_MAX_LENGTH} character limit (${len} characters).`;

const DISPLAY_NAME_EMPTY_WARNING = 'Display Name should not be empty.';
const TYPE_EMPTY_WARNING = 'Type must be specified (Possessive or Generic).';
const DURATION_DAYS_MUST_BE_POSITIVE = 'Duration Days must be more than zero.';
const ACTOR_THRESHOLD_MUST_BE_POSITIVE = 'Actor Threshold must be more than zero.';
const IS_SYSTEM_INFO = 'This is a system-managed approval type. Core fields may be locked by the backend.';
const POSSESSIVE_SCOPE_EMPTY_WARNING = 'Possessive Scope should be set when Type is Possessive.';

export interface RequestApprovalTypeValidationOptions {
    /** True when dialog is open in clone mode (create from existing). */
    isCloneMode?: boolean;
    /** Display name of the entity being cloned (when isCloneMode). */
    existingEntityDisplayName?: string;
}

/**
 * Returns validation warnings for RequestApprovalType entity data.
 */
export function getRequestApprovalTypeWarnings(
    data: Record<string, any>,
    options?: RequestApprovalTypeValidationOptions
): ValidationWarning[] {
    const warnings: ValidationWarning[] = [];

    if (options?.isCloneMode && options?.existingEntityDisplayName) {
        warnings.push({
            message: CLONE_DISPLAY_NAME_WARNING,
            severity: 'warning',
            attributeDisplayNames: ['Display Name']
        });
    }

    const displayName = getEntityAttrString(data, 'DisplayName');
    if (!displayName.trim()) {
        warnings.push({
            message: DISPLAY_NAME_EMPTY_WARNING,
            severity: 'warning',
            attributeDisplayNames: ['Display Name']
        });
    }

    const typeVal = getEntityAttrString(data, 'Type');
    if (!typeVal.trim()) {
        warnings.push({
            message: TYPE_EMPTY_WARNING,
            severity: 'warning',
            attributeDisplayNames: ['Type']
        });
    }

    const tagsRaw = getEntityAttr(data, 'Tags');
    const tagsStr =
        typeof tagsRaw === 'string'
            ? tagsRaw
            : Array.isArray(tagsRaw)
                ? (tagsRaw as string[]).join(', ')
                : '';
    const tagsLength = tagsStr.length;
    if (tagsLength > TAGS_MAX_LENGTH) {
        warnings.push({
            message: TAGS_LENGTH_WARNING(tagsLength),
            severity: 'warning',
            attributeDisplayNames: ['Tags']
        });
    }

    const durationDaysRaw = getEntityAttr(data, 'DurationDays');
    const durationDays = typeof durationDaysRaw === 'number' ? durationDaysRaw : (durationDaysRaw !== undefined && durationDaysRaw !== null && durationDaysRaw !== '' ? Number(durationDaysRaw) : NaN);
    if (!Number.isNaN(durationDays) && durationDays <= 0) {
        warnings.push({
            message: DURATION_DAYS_MUST_BE_POSITIVE,
            severity: 'warning',
            attributeDisplayNames: ['Duration Days']
        });
    }

    const actorThresholdRaw = getEntityAttr(data, 'ActorThreshold');
    const actorThreshold = typeof actorThresholdRaw === 'number' ? actorThresholdRaw : (actorThresholdRaw !== undefined && actorThresholdRaw !== null && actorThresholdRaw !== '' ? Number(actorThresholdRaw) : NaN);
    if (!Number.isNaN(actorThreshold) && actorThreshold <= 0) {
        warnings.push({
            message: ACTOR_THRESHOLD_MUST_BE_POSITIVE,
            severity: 'warning',
            attributeDisplayNames: ['Actor Threshold']
        });
    }

    const isSystemRaw = getEntityAttr(data, 'IsSystem');
    const isSystem = typeof isSystemRaw === 'boolean' ? isSystemRaw : (typeof isSystemRaw === 'string' ? isSystemRaw.toLowerCase() === 'true' : !!isSystemRaw);
    if (isSystem) {
        warnings.push({
            message: IS_SYSTEM_INFO,
            severity: 'info',
            attributeDisplayNames: ['Is System']
        });
    }

    const typeLower = typeVal.trim().toLowerCase();
    if (typeLower === 'possessive') {
        const possessiveScope = getEntityAttrString(data, 'PossessiveScope');
        if (!possessiveScope.trim()) {
            warnings.push({
                message: POSSESSIVE_SCOPE_EMPTY_WARNING,
                severity: 'warning',
                attributeDisplayNames: ['Possessive Scope']
            });
        }
    }

    return warnings;
}
